import React, { createContext, useReducer, useContext, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  initialState,
  SnackbarState,
  reducer,
  enqueueSnackbar as enqueueSnackbarAction,
  closeSnackbar as closeSnackbarAction,
  Snackbar,
} from './snackbarReducer';

interface SnackbarsContextInterface {
  snackbarVisible: boolean;
  state: SnackbarState;
  enqueueSnackbar: (snackbar: Snackbar) => void;
  closeSnackbar: (key?: string) => void;
}

export const SnackbarsContext = createContext<SnackbarsContextInterface>({
  snackbarVisible: false,
  state: initialState,
  enqueueSnackbar: () => {},
  closeSnackbar: () => {},
});

type SnackbarTimeoutIDs = Record<string, number>;

export const SnackbarStoreProvider = ({
  children,
}: React.PropsWithChildren): React.ReactElement<any> => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const snackbarTimeoutIDsRef = useRef<SnackbarTimeoutIDs>({});

  const closeSnackbar = (key?: string) => {
    const snackbarTimeoutIDs = snackbarTimeoutIDsRef.current;
    if (key) {
      const timeoutId = snackbarTimeoutIDs[key];
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      delete snackbarTimeoutIDs[key];
    } else {
      Object.values(snackbarTimeoutIDs).forEach((timeoutId) =>
        clearTimeout(timeoutId)
      );
      snackbarTimeoutIDsRef.current = {};
    }
    dispatch(closeSnackbarAction(key));
  };

  const enqueueSnackbar = ({
    dismissible = true,
    key = uuidv4(),
    ...snackbarOg
  }: Snackbar) => {
    const snackbar = { ...snackbarOg, dismissible, key };

    dispatch(enqueueSnackbarAction(snackbar));

    if (snackbar.autoHideDuration && snackbar.key) {
      const snackbarKey = snackbar.key;
      const snackbarTimeoutIDs = snackbarTimeoutIDsRef.current;
      const existingTimeoutId = snackbarTimeoutIDs[snackbarKey];
      if (existingTimeoutId) {
        clearTimeout(existingTimeoutId);
      }
      const timeoutId = setTimeout(() => {
        closeSnackbar(snackbarKey);
      }, snackbar.autoHideDuration);
      snackbarTimeoutIDs[snackbarKey] = timeoutId;
    }
  };

  const snackbarVisible = state.snackbars.length > 0;

  return (
    <SnackbarsContext.Provider
      value={{ state, enqueueSnackbar, closeSnackbar, snackbarVisible }}
    >
      {children}
    </SnackbarsContext.Provider>
  );
};

export const useSnackbarsContext = () => useContext(SnackbarsContext);
